// src/pages/Appli/AppliAdmin/ListingClients/components/AssociationActions.jsx

import React from "react";

import {
  FaEdit,
  FaEnvelope,
  FaEye,
  FaGlobe,
  FaPhoneAlt,
  FaSms,
  FaSpinner,
  FaTrash,
} from "react-icons/fa";

const BASE_CLASS = [
  "inline-flex h-9 w-9 items-center justify-center",
  "rounded-xl border text-sm",
  "transition focus:outline-none focus:ring-4",
  "disabled:cursor-not-allowed disabled:opacity-50",
].join(" ");

const TONES = {
  neutral:
    "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 focus:ring-slate-500/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white",

  blue:
    "border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 focus:ring-blue-500/10 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300 dark:hover:bg-blue-500/20",

  green:
    "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 focus:ring-emerald-500/10 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/20",

  violet:
    "border-violet-200 bg-violet-50 text-violet-600 hover:bg-violet-100 focus:ring-violet-500/10 dark:border-violet-500/20 dark:bg-violet-500/10 dark:text-violet-300 dark:hover:bg-violet-500/20",

  red:
    "border-red-200 bg-red-50 text-red-600 hover:bg-red-100 focus:ring-red-500/10 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20",
};

function normalizePhone(value) {
  return String(value || "").replace(
    /[^\d+]/g,
    "",
  );
}

function normalizeWebsite(value) {
  const url = String(value || "").trim();

  if (!url) return "";

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  return `https://${url}`;
}

function ActionButton({
  title,
  tone = "neutral",
  disabled = false,
  onClick,
  children,
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className={`${BASE_CLASS} ${TONES[tone]}`}
    >
      {children}
    </button>
  );
}

function ActionLink({
  href,
  title,
  tone = "neutral",
  children,
}) {
  return (
    <a
      href={href}
      title={title}
      aria-label={title}
      target={
        href.startsWith("http") ? "_blank" : undefined
      }
      rel={
        href.startsWith("http")
          ? "noopener noreferrer"
          : undefined
      }
      className={`${BASE_CLASS} ${TONES[tone]}`}
    >
      {children}
    </a>
  );
}

export default function AssociationActions({
  association,
  deleting = false,
  onQuickView,
  onEdit,
  onDelete,
  className = "",
}) {
  if (!association) return null;

  const phone = normalizePhone(
    association.tel_asso,
  );

  const website = normalizeWebsite(
    association.siteweb_asso,
  );

  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 ${className}`}
    >
      <ActionButton
        title="Voir la fiche"
        onClick={() =>
          onQuickView?.(association)
        }
      >
        <FaEye />
      </ActionButton>

      <ActionButton
        title="Modifier"
        tone="violet"
        onClick={() =>
          onEdit?.(association.id, association)
        }
      >
        <FaEdit />
      </ActionButton>

      {website ? (
        <ActionLink
          href={website}
          title="Ouvrir le site"
          tone="blue"
        >
          <FaGlobe />
        </ActionLink>
      ) : null}

      {phone ? (
        <>
          <ActionLink
            href={`tel:${phone}`}
            title="Téléphoner"
            tone="green"
          >
            <FaPhoneAlt />
          </ActionLink>

          <ActionLink
            href={`sms:${phone}`}
            title="Envoyer un SMS"
            tone="green"
          >
            <FaSms />
          </ActionLink>
        </>
      ) : null}

      {association.mail_asso ? (
        <ActionLink
          href={`mailto:${association.mail_asso}`}
          title="Envoyer un email"
          tone="blue"
        >
          <FaEnvelope />
        </ActionLink>
      ) : null}

      <ActionButton
        title="Supprimer"
        tone="red"
        disabled={deleting}
        onClick={() =>
          onDelete?.(association.id)
        }
      >
        {deleting ? (
          <FaSpinner className="animate-spin" />
        ) : (
          <FaTrash />
        )}
      </ActionButton>
    </div>
  );
}